import type { CarouselSlide } from '@shared/components/carousel/Carousel';
import { getRestaurantImagePath } from '@shared/utils/getRestaurantImagePath';
import { useMemo } from 'react';
import { useAvailabilityContext } from 'webReservations/context/AvailabilityContext';
import type { Availability } from 'webReservations/restaurant/apiHelpers';

/** Carousel copy, tags, remount key, and listing title from availability context for the selected table. */
export interface TableSelectedPresentation {
  selectedTablePhotoSlides: CarouselSlide[];
  selectedTableDescription: string | null;
  selectedTableTags: string[];
  carouselRemountKey: string;
  selectedTablePublicName: string | undefined;
}

type AvailabilitiesByTime = Record<string, Availability[]>;

/** Table listing display name when exactly one floor-plan listing is selected. */
const getSelectedTablePublicName = (
  selectedFloorPlanListingIds: string[],
  filteredAvailabilities: Availability[],
  filteredAvailabilitiesGroupedByTime: AvailabilitiesByTime,
  selectedTime: string | null | undefined,
): string | undefined => {
  if (selectedFloorPlanListingIds.length !== 1) {
    return undefined;
  }
  const listingId = selectedFloorPlanListingIds[0];
  if (selectedTime) {
    const atTime = filteredAvailabilitiesGroupedByTime[selectedTime] ?? [];
    const fromTime = atTime.find((a) => a.listing.id === listingId);
    const raw =
      fromTime?.listing.publicName ??
      filteredAvailabilities.find((a) => a.listing.id === listingId)?.listing
        .publicName;
    const trimmed = raw?.trim();
    return trimmed || undefined;
  }
  const raw = filteredAvailabilities.find((a) => a.listing.id === listingId)
    ?.listing.publicName;
  const trimmed = raw?.trim();
  return trimmed || undefined;
};

const EMPTY_PRESENTATION: TableSelectedPresentation = {
  selectedTablePhotoSlides: [],
  selectedTableDescription: null,
  selectedTableTags: [],
  carouselRemountKey: '',
  selectedTablePublicName: undefined,
};

/**
 * Returns the `Availability` row for the chosen floor-plan listing in this
 * time bucket. We only show table photos/copy when exactly one listing is
 * selected; best-available mode (no listing ids) is handled upstream.
 */
const findAvailabilityForTablePresentation = (
  availabilities: Availability[],
  selectedFloorPlanListingIds: string[],
): Availability | undefined => {
  if (selectedFloorPlanListingIds.length !== 1) {
    return undefined;
  }
  return availabilities.find(
    (a) => a.listing.id === selectedFloorPlanListingIds[0],
  );
};

/**
 * Order of times to inspect: pinned `selectedTime` if that bucket exists,
 * otherwise every sorted time that contains the selected listing.
 */
const resolveTimesToScan = (
  grouped: AvailabilitiesByTime,
  selectedTime: string | null | undefined,
  selectedFloorPlanListingIds: string[],
): string[] => {
  const sortedTimeKeys = Object.keys(grouped).sort();

  if (selectedTime && grouped[selectedTime] != null) {
    return [selectedTime];
  }

  const listingId = selectedFloorPlanListingIds[0];
  return sortedTimeKeys.filter((time) =>
    (grouped[time] ?? []).some((a) => a.listing.id === listingId),
  );
};

const normalizeTableTags = (tags: string[] | undefined): string[] =>
  (tags ?? []).map((t) => t.trim()).filter(Boolean);

const buildCarouselSlides = (
  images: NonNullable<Availability['listing']['tableImages']>,
): CarouselSlide[] => {
  const slides: CarouselSlide[] = [];
  for (const image of [...images].sort((a, b) => a.position - b.position)) {
    if (image.imageKey) {
      slides.push({
        key: image.id,
        backgroundImage: `url(${getRestaurantImagePath(image.imageKey)})`,
      });
    }
  }
  return slides;
};

/** Forces `Carousel` to remount when the logical selection changes, not only when slide count changes. */
const buildCarouselRemountKey = (
  selectedListingId: string,
  selectedTime: string | null | undefined,
): string => `${selectedListingId}|${selectedTime ?? 'all'}`;

/**
 * Guest UI only shows this strip when exactly one floor-plan listing is
 * selected and that slot has at least one image. Best-available mode (no
 * listing selection) never shows table photos or copy here.
 */
export const computeTableSelectedPresentation = (
  grouped: AvailabilitiesByTime,
  selectedFloorPlanListingIds: string[],
  selectedTime: string | null | undefined,
): TableSelectedPresentation => {
  if (selectedFloorPlanListingIds.length !== 1) {
    return EMPTY_PRESENTATION;
  }

  const timesToScan = resolveTimesToScan(
    grouped,
    selectedTime,
    selectedFloorPlanListingIds,
  );

  let tableDescription: string | null | undefined;
  let tableTags: string[] = [];
  const slides: CarouselSlide[] = [];

  for (const time of timesToScan) {
    const availabilities = grouped[time];
    const availabilityForPresentation = findAvailabilityForTablePresentation(
      availabilities,
      selectedFloorPlanListingIds,
    );
    tableDescription =
      availabilityForPresentation?.listing.tableDescription ?? undefined;
    const images = availabilityForPresentation?.listing.tableImages;
    if (!images?.length) {
      continue;
    }
    tableTags = normalizeTableTags(
      availabilityForPresentation?.listing.tableTags,
    );
    slides.push(...buildCarouselSlides(images));
    break;
  }

  const trimmed =
    typeof tableDescription === 'string' ? tableDescription.trim() : '';

  return {
    selectedTablePhotoSlides: slides,
    selectedTableDescription: trimmed.length ? trimmed : null,
    selectedTableTags: tableTags,
    carouselRemountKey: buildCarouselRemountKey(
      selectedFloorPlanListingIds[0],
      selectedTime,
    ),
    selectedTablePublicName: undefined,
  };
};

export const useTableSelectedPresentation = (): TableSelectedPresentation => {
  const {
    filteredAvailabilities,
    filteredAvailabilitiesGroupedByTime,
    selectedFloorPlanListingIds,
    selectedTime,
  } = useAvailabilityContext();

  return useMemo(() => {
    const base = computeTableSelectedPresentation(
      filteredAvailabilitiesGroupedByTime,
      selectedFloorPlanListingIds,
      selectedTime,
    );
    return {
      ...base,
      selectedTablePublicName: getSelectedTablePublicName(
        selectedFloorPlanListingIds,
        filteredAvailabilities,
        filteredAvailabilitiesGroupedByTime,
        selectedTime,
      ),
    };
  }, [
    filteredAvailabilities,
    filteredAvailabilitiesGroupedByTime,
    selectedFloorPlanListingIds,
    selectedTime,
  ]);
};
